import { SHAlert, SHDivider, SHStack } from "@components/design-systems";
import { SHCollapse } from "@components/design-systems/sh-collapse";
import { WarningSVG } from "@components/svgs";
import { SHFieldGroupMemo } from "@layouts/form/field-group-memo";
import { AnalysisUserType } from "@models/platform-analysis/entities/analysis";
import { FeeCalculatorRenderSectionConfig } from "@models/platform-analysis/entities/steps/fee";
import { Collapse } from "@mui/material";
import { isEqual } from "lodash";
import { memo, useState } from "react";
import { TransitionGroup } from "react-transition-group";
import { useToggle } from "react-use";
import { FeeCalculatorAutoCalculatedBadge } from "../rendered-fields/auto-calculated-badge";
import { FeeCalculatorErrorStatesTracker } from "../trackers/error-states-tracker";
import { FeeCalculatorRenderedFieldWrapper } from "../rendered-fields/rendered-field-wrapper";
import { FeeCalculatorVisibilityTracker } from "../trackers/visibility-tracker";
import { FeeCalculatorSectionId } from "@models/platform-analysis/enums/fee/feeCalculatorSectionId";

type Props = {
  sectionConfig: FeeCalculatorRenderSectionConfig;
  userType: AnalysisUserType;
  disabled?: boolean;
};
export const FeeCalculatorSection = memo(
  ({ sectionConfig, userType, disabled }: Props) => {
    const {
      id,
      titles: { main: mainTitle, subTitle },
      defaultVisibility,
      renderConditions: requiredConditions,
      fields = [],
      validateStateConditions,
      calculationBadge,
    } = sectionConfig;

    const [shouldRenderSection, toggleRenderSection] = useToggle(
      !!defaultVisibility,
    );
    const [sectionErrorStates, setSectionErrorStates] = useState<string[]>([]);

    const handleOnVisibilityChange = (isVisible: boolean) => {
      if (isVisible === shouldRenderSection) return;
      toggleRenderSection();
    };

    const handleOnErrorStatesChange = (errorStates: string[]) => {
      if (id === FeeCalculatorSectionId.PortfolioSetup) return;
      if (isEqual(sectionErrorStates, errorStates)) return;
      setSectionErrorStates(errorStates);
    };

    return (
      <>
        <FeeCalculatorVisibilityTracker
          renderConditions={requiredConditions}
          onVisibilityChange={handleOnVisibilityChange}
        />

        <FeeCalculatorErrorStatesTracker
          sectionId={id}
          validateStateConditions={validateStateConditions}
          onErrorStatesChange={handleOnErrorStatesChange}
          isPortfolioSetupSection={id === FeeCalculatorSectionId.PortfolioSetup}
        />

        <SHCollapse
          open={shouldRenderSection}
          displayArrow={false}
          collapseProps={{
            unmountOnExit: true,
          }}
        >
          <SHDivider />
          <SHFieldGroupMemo
            title={mainTitle[userType]}
            subTitle={subTitle?.[userType]}
            maxWidth={520}
          >
            <SHStack gap={2}>
              <SHStack
                width={"100%"}
                alignItems={"start"}
                display={"grid"}
                gridTemplateColumns={"repeat(2, 1fr)"}
                columnGap={4}
                rowGap={3}
                sx={{ width: { xs: "100%", md: 520 } }}
              >
                {fields.map((fieldConfig, index) => (
                  <FeeCalculatorRenderedFieldWrapper
                    key={index}
                    disabled={disabled}
                    fieldConfig={fieldConfig}
                  />
                ))}
              </SHStack>

              <TransitionGroup
                style={{
                  maxWidth: 520,
                  display: "flex",
                  flexDirection: "column",
                  gap: sectionErrorStates.length > 0 ? "5px" : 0,
                }}
              >
                {sectionErrorStates.map((errorMessage) => (
                  <Collapse key={errorMessage}>
                    <SHAlert
                      severity="error"
                      textAlign={"left"}
                      icon={<WarningSVG />}
                    >
                      {errorMessage}
                    </SHAlert>
                  </Collapse>
                ))}
              </TransitionGroup>

              {!!calculationBadge && (
                <FeeCalculatorAutoCalculatedBadge {...calculationBadge} />
              )}
            </SHStack>
          </SHFieldGroupMemo>
        </SHCollapse>
      </>
    );
  },
);
